// --- Base Models (Output from API) ---

export interface UserModel {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  password?: string; // Pouze pro Create
}

export interface PasswordUpdateModel {
  userId: string;
  newPassword: string;
}

export interface UserLiteModel {
  id: string;
  firstName: string;
  lastName: string;
}

export interface ClubModel {
  id: number;
  name: string;
  faNumber?: string;
  isInternal: boolean;
  logoUrl?: string;
}

export interface PlayerModel {
  id: number;
  firstName: string;
  lastName: string;
  dateOfBirth?: string | Date;
  facrId?: string;
  currentClubId?: number;
  currentClub?: ClubModel;
  position?: string;
  isActive: boolean;
  transfers: TransferModel[];
  attachmentAssignments: AttachmentAssignmentModel[];
  created: string | Date;
  updated: string | Date;
}

export interface AttachmentModel {
  id?: number;
  fileName: string;
  storageId: string;
  mimeType: string;
  storageType: number;
  fileSize: number;
  uploadedAt: string | Date;
  url: string;
  thumbnailUrl?: string;
}

export interface AttachmentAssignmentModel {
  id?: number;
  attachmentId: number;
  attachment?: AttachmentModel;
  transferId?: number;
  playerId?: number;
  commentId?: number;
  assignedAt: string | Date;
}

export interface CommentModel {
  id?: number;
  content: string;
  createdAt: string | Date;
  authorUserId?: string;
  transferId?: number;

  user?: UserLiteModel;
}

export interface TransferModel {
  id: number;
  playerId: number;
  player?: PlayerModel;
  fromClubId: number;
  fromClub?: ClubModel;
  toClubId: number;
  toClub?: ClubModel;
  date: string | Date;
  type: TransferType;
  status: TransferStatus;
  loanEndDate?: string | Date;
  isLoanActive: boolean;
  feeType: FeeType;
  amount?: number;
  amountPerMatch?: number;
  currency: string;
  note?: string;
  attachmentAssignments: AttachmentAssignmentModel[];
  comments: CommentModel[];
  created: string | Date;
  updated: string | Date;
}

// --- Transfer Period ---

export interface TransferPeriod {
  id: string;
  label: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
}

// --- DataListResponse (Generic response wrapper) ---

export interface DataListResponse<T> {
  data: T[];
  totalRecords: number;
  page: number;
  pageSize: number;
}

// --- Enums ---

export enum StorageType {
  Local = 1,
  GoogleDrive = 2
}

export enum AttachmentOwnerType {
  Transfer = 1,
  Player = 2,
  Comment = 3
}

export enum TransferType {
  Permanent = 1,
  Loan = 2,
  CooperationAgreement = 3
}

export enum FeeType {
  Free = 1,
  Fixed = 2,
  PerMatch = 3
}

export enum TransferStatus {
  ToResolve = 1,
  PendingOtherSide = 2,
  PendingOurSignatures = 3,
  PendingFacrConfirmation = 4,
  Resolved = 5,
  Cancelled = 6
}

// --- Contract Models ---

export interface ContractTemplateModel {
  id?: number;
  name: string;
  description?: string;
  applicableTransferType?: number;
  isActive: boolean;
  sections: ContractSectionModel[];
  createdAt: string | Date;
  updatedAt: string | Date;
  createdByUserId: string;
}

export interface ContractSectionModel {
  id?: number;
  templateId?: number;
  title: string;
  order: number;
  type: ContractSectionType;
  isOptional: boolean;
  blocks: ContractBlockModel[];
}

export interface ContractBlockModel {
  id?: number;
  sectionId?: number;
  content: string;
  order: number;
  type: ContractBlockType;
  width?: number;
  height?: number;
  referenceId?: string;
}

export interface ContractModel {
  id?: number;
  transferId: number;
  transfer?: TransferModel;
  templateId?: number;
  title: string;
  renderedContent: string;
  placeholderValuesJson: string;
  templateSnapshotJson: string;
  status: ContractStatus;
  pdfAttachmentId?: number;
  pdfAttachment?: AttachmentModel;
  createdAt: string | Date;
  updatedAt: string | Date;
  finalizedAt?: string | Date;
  createdByUserId: string;
  placeholderValues: ContractValueModel[];
}

export interface ContractValueModel {
  id?: number;
  contractId?: number;
  placeholderKey: string;
  value: string;
  attachmentId?: number;
}

export interface PlaceholderDefinition {
  key: string;
  label: string;
  type: string;
  value?: string;
  isRequired: boolean;
  group?: string;
}

export enum ContractSectionType {
  Standard = 0,
  Header = 1,
  Parties = 2,
  Terms = 3,
  Sanctions = 4,
  Final = 5,
  Signatures = 6
}

export enum ContractBlockType {
  Text = 0,
  NumberedPoint = 1,
  Image = 2,
  Signature = 3,
  Table = 4,
  Divider = 5,
  BonusClause = 6
}

export enum ContractStatus {
  Draft = 0,
  Finalized = 1,
  Signed = 2,
  Cancelled = 3
}

// --- Contract Filter Params ---

export interface ContractFilterParams {
  page?: number;
  pageSize?: number;
  search?: string;
  sortField?: string;
  sortOrder?: number;
  transferId?: number;
  status?: number;
}

export interface ContractTemplateFilterParams {
  page?: number;
  pageSize?: number;
  search?: string;
  sortField?: string;
  sortOrder?: number;
  transferType?: number;
  isActive?: boolean;
}
